import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SVGIconType } from '../../../constants/svgIcon';
import { RewardReferralRole } from '../../../models/RewardStore';
import { SvgIcon } from '../../Molecules';
import {
  BasicPopupContainer,
  PopUpButtonColors,
} from '../Containers/BasicPopupContainer';

import styles from './DistributorReferralRewardsPopup.module.css';

type DistributorReferralRewardsPopupProps = {
  role: RewardReferralRole;
  onPrimary: () => void;
  onClickViewDetails: () => void;
};

// export enum RewardReferralRole {
//   Partner = 'partner',
//   Distributor = 'distributor',
//   Customer = 'customer',
// }

export const DistributorReferralRewardsPopup = (
  props: DistributorReferralRewardsPopupProps,
) => {
  const { role, onPrimary, onClickViewDetails } = props;

  const { t } = useTranslation();

  const roleName = useMemo(() => {
    return role === RewardReferralRole.Distributor
      ? 'Distributor'
      : role === RewardReferralRole.Partner
      ? 'Partner'
      : '';
  }, [role]);

  const roleNameInverse = useMemo(() => {
    return role === RewardReferralRole.Distributor
      ? 'Partner'
      : role === RewardReferralRole.Partner
      ? 'Distributor'
      : '';
  }, [role]);

  const hardcodedNumbers = {
    Distributor: {
      distributorReferralReward: 'HKD3,000',
      partnerReferralReward: 'HKD5,000',
      T1Percentage: '25%',
      T2Percentage: '15%',
    },
    Partner: {
      partnerReferralReward: 'HKD5,500',
      distributorReferralReward: 'HKD3,300',
      managementRewardPercentage: '10%',
    },
    Customer: {
      T1Percentage: '10%',
      T2Percentage: '5%',
    },
  };

  return (
    <BasicPopupContainer
      primaryButtonColor={PopUpButtonColors.SUCCESS}
      onPrimaryButtonClick={onPrimary}
    >
      <div className={styles.popupContent}>
        <SvgIcon
          name={SVGIconType.Referral}
          className={styles.logo}
          color="#333"
        />

        <div className={styles.title}>
          {roleName ? t(`${roleName} Referral Rewards`) : t('Referral Rewards')}
        </div>

        {role !== RewardReferralRole.Customer && (
          <div className={styles.greyBox}>
            <div className={styles.grayBoxTitle}>
              {t('Rewards Per Referred Users')}
            </div>

            <hr />

            <div className={styles.innerBox}>
              <div className={styles.innerTitle}>
                {t(`${roleName} Referral Reward:`)}
              </div>
              <div className={styles.innerMain}>
                {role === RewardReferralRole.Distributor
                  ? hardcodedNumbers.Distributor.distributorReferralReward
                  : hardcodedNumbers.Partner.partnerReferralReward}
              </div>
              <div className={styles.innerMinor}>
                {t(`Your Referred ${roleName}`)}
              </div>
            </div>

            <hr />

            <div className={styles.innerBox}>
              <div className={styles.innerTitle}>
                {t(`${roleNameInverse} Referral Reward:`)}
              </div>
              <div className={styles.innerMain}>
                {role === RewardReferralRole.Distributor
                  ? hardcodedNumbers.Distributor.partnerReferralReward
                  : hardcodedNumbers.Partner.distributorReferralReward}
              </div>
              <div className={styles.innerMinor}>
                {t(`Your Referred ${roleNameInverse}`)}
              </div>
            </div>
          </div>
        )}

        {role === RewardReferralRole.Distributor ? (
          <div className={styles.greyBox}>
            <div className={styles.grayBoxTitle}>
              {t('Rewards Per Product Purchases from Referred Users')}
            </div>

            <hr />

            <div className={styles.innerBox}>
              <div className={styles.innerTitle}>
                {t('Tier 1 Referral Reward:')}
              </div>
              <div className={styles.innerMain}>
                {hardcodedNumbers.Distributor.T1Percentage}
              </div>
              <div className={styles.innerMinor}>
                {t('Your Referred Users')}
              </div>
            </div>

            <hr />

            <div className={styles.innerBox}>
              <div className={styles.innerTitle}>
                {t('Tier 2 Referral Reward:')}
              </div>
              <div className={styles.innerMain}>
                {hardcodedNumbers.Distributor.T2Percentage}
              </div>
              <div className={styles.innerMinor}>
                {t('The Referred Users of Your Referred Users')}
              </div>
            </div>
          </div>
        ) : role === RewardReferralRole.Partner ? (
          <div className={styles.greyBox}>
            <div className={styles.grayBoxTitle}>
              {t('Rewards Based on Referred Distributors’ Net Sales Revenue')}
            </div>

            <hr />

            <div className={styles.innerBox}>
              <div className={styles.innerTitle}>{t('Management Reward:')}</div>
              <div className={styles.innerMain}>
                {hardcodedNumbers.Partner.managementRewardPercentage}
              </div>
              <div className={styles.innerMinor}>
                {t('Your Referred Distributor')}
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.greyBox}>
            <div className={styles.grayBoxTitle}>
              {t('Rewards Per Product Purchases from Referred Users')}
            </div>

            <hr />

            <div className={styles.innerBox}>
              <div className={styles.innerTitle}>
                {t('Tier 1 Referral Reward:')}
              </div>
              <div className={styles.innerMain}>
                {hardcodedNumbers.Customer.T1Percentage}
              </div>
              <div className={styles.innerMinor}>
                {t('Your Referred Users')}
              </div>
            </div>

            <hr />

            <div className={styles.innerBox}>
              <div className={styles.innerTitle}>
                {t('Tier 2 Referral Reward:')}
              </div>
              <div className={styles.innerMain}>
                {hardcodedNumbers.Customer.T2Percentage}
              </div>
              <div className={styles.innerMinor}>
                {t('The Referred Users of Your Referred Users')}
              </div>
            </div>
          </div>
        )}

        <div className={styles.viewButton} onClick={onClickViewDetails}>
          {t('View Details')}
        </div>
      </div>
    </BasicPopupContainer>
  );
};
